import type { LucideIcon } from 'lucide-react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'

export type DropdownSelectOption = {
  value: string
  label: string
}

type DropdownSelectProps = {
  value: string
  onChange: (value: string) => void
  options: DropdownSelectOption[]
  icon?: LucideIcon
  ariaLabel: string
  menuTitle?: string
  align?: 'left' | 'right'
  className?: string
  disabled?: boolean
  fullWidth?: boolean
  size?: 'sm' | 'md'
  stopPropagation?: boolean
}

const EMPTY_VALUE = '__dropdown_empty__'

/** 列表表头行 — shadcn 语义 token */
export const listTableHeadRowClass =
  'border-b border-border bg-muted/25 text-[11px] uppercase tracking-wide text-muted-foreground'

/** 列表数据行 */
export const listTableRowClass = 'border-b border-border/60 last:border-0 hover:bg-muted/20'

function toSelectValue(value: string): string {
  return value === '' ? EMPTY_VALUE : value
}

function fromSelectValue(value: string): string {
  return value === EMPTY_VALUE ? '' : value
}

/** 门户 / 账户单选下拉 — shadcn Select + Radix */
export function DropdownSelect({
  value,
  onChange,
  options,
  icon: Icon,
  ariaLabel,
  menuTitle,
  align = 'left',
  className,
  disabled = false,
  fullWidth = false,
  size = 'md',
  stopPropagation = false,
}: DropdownSelectProps) {
  return (
    <Select
      value={toSelectValue(value)}
      onValueChange={(next) => onChange(fromSelectValue(next))}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        size={size === 'sm' ? 'sm' : 'default'}
        className={cn(
          fullWidth && 'w-full',
          align === 'right' && 'ml-auto',
          className,
        )}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation()
        }}
      >
        {Icon ? <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
        <SelectValue placeholder={options[0]?.label ?? '—'} />
      </SelectTrigger>
      <SelectContent align={align === 'right' ? 'end' : 'start'}>
        {menuTitle ? (
          <p className="px-2 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {menuTitle}
          </p>
        ) : null}
        {options.map((opt) => (
          <SelectItem key={opt.value || EMPTY_VALUE} value={toSelectValue(opt.value)}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
